// @flow
import React, {type Node, Fragment} from 'react'
import {type RelayFragmentContainer, createFragmentContainer, graphql} from 'react-relay'

import useEnterOverride from 'utils/hooks/useEnterOverride'
import type {PickAlert} from 'components/proposals/types'
import Accountabilities from 'components/proposals/Part/shared/Editor/Accountabilities'
import Domains from 'components/proposals/Part/shared/Editor/Domains'
import RoleName from 'components/proposals/Part/shared/Editor/RoleName'
import RolePurpose from 'components/proposals/Part/shared/Editor/RolePurpose'

import RoleTemplateSelector, {
  type RoleTemplateFields,
} from 'components/proposals/Part/shared/Editor/RoleTemplateSelector'

import Footer from './Footer'
import PasteRoleButton from './PasteRoleButton'
import {mergeRoleText, clearRoleFields, mergeRoleTemplate, areFieldsEmpty} from './roleFieldsHelpers'
import styles from './index.scss'
import type {PartProps} from '../types'
import type {Editor_proposal as Proposal} from './__generated__/Editor_proposal.graphql'

/* eslint-disable react/default-props-match-prop-types */

type Props = $ReadOnly<{
  ...PartProps,
  pickAlert: PickAlert,
  proposal: Proposal,
  roleTemplateId: ?string,
}>

function Editor({
  destroyRole,
  destroyCircle,
  isReadonly,
  mergeUpdate,
  organization,
  roleTemplateId,
  isNew,
  ...props
}: Props): Node {
  const [enterOverride, changeEnterOverride] = useEnterOverride()
  const isRemovingRoleOrCircle = destroyRole || destroyCircle
  const isDisabled = isReadonly || isRemovingRoleOrCircle

  const pasteRole = (roleText: string) => mergeUpdate(mergeRoleText(roleText, props))

  const selectTemplate = (id: ?(string | boolean), fields: ?RoleTemplateFields) => {
    if (id === roleTemplateId)
      return

    const confirmationMsg = fields
      ? I18n.t('side_nav.settings_section.role_templates.warning_replace_content')
      : I18n.t('side_nav.settings_section.role_templates.warning_clear_content')

    const getNewFields = () => (fields ? mergeRoleTemplate(fields, props) : clearRoleFields(props))

    // eslint-disable-next-line no-alert
    if (areFieldsEmpty(props) || window.confirm(confirmationMsg)) {
      mergeUpdate(getNewFields())
      props.onChange(['roleTemplateId'], id)
    }
  }

  const renderPasteRoleButton = () => (
    isDisabled
      ? null
      : <PasteRoleButton onClick={pasteRole} />
  )

  return (
    <div className="edit-role edit-role-test">
      {isNew && (
        <RoleTemplateSelector
          organization={organization}
          roleTemplateId={roleTemplateId}
          onChange={selectTemplate}
        />
      )}
      <RoleName
        circle={props.proposal.circle}
        disabled={isDisabled || props.isProtected}
        id={`EditRole-${props.changeKey}-name`}
        name={props.name}
        onChange={props.onChange}
        organization={organization}
        pickAlert={props.pickAlert}
        renderPasteRoleButton={renderPasteRoleButton}
        strikethrough={isRemovingRoleOrCircle}
      />
      {organization.showPurpose && (
        <RolePurpose
          circle={props.proposal.circle}
          disabled={isDisabled || props.isProtected}
          id={`EditRole-${props.changeKey}-purpose`}
          label={organization.purposeLabel}
          onChange={props.onChange}
          organization={organization}
          pickAlert={props.pickAlert}
          purpose={props.purpose}
          strikethrough={isRemovingRoleOrCircle}
        />
      )}
      {organization.showDomains && (
        <div className={styles.withMargin}>
          <Domains
            circle={props.proposal.circle}
            domains={props.domains}
            isReadonly={isDisabled}
            isRemovingRoleOrCircle={isRemovingRoleOrCircle}
            label={organization.domainsLabel}
            onChange={props.onChange}
            onDelete={props.onDelete}
            organization={organization}
            pickAlert={props.pickAlert}
            enterOverride={enterOverride}
            changeEnterOverride={changeEnterOverride}
          />
        </div>
      )}
      {organization.showAccountabilities && (
        <Accountabilities
          accountabilities={props.accountabilities}
          circle={props.proposal.circle}
          isReadonly={isDisabled}
          isRemovingRoleOrCircle={isRemovingRoleOrCircle}
          onChange={props.onChange}
          onDelete={props.onDelete}
          organization={organization}
          pickAlert={props.pickAlert}
          enterOverride={enterOverride}
          changeEnterOverride={changeEnterOverride}
        />
      )}
      {!props.isProtected && (
        <Fragment>
          <hr />
          <Footer
            changeKey={props.changeKey}
            roleName={props.name.nextValue}
            destroyCircle={destroyCircle}
            destroyCircleKeepRole={props.destroyCircleKeepRole}
            destroyRole={destroyRole}
            expandRole={props.expandRole}
            restructureCircle={props.restructureCircle}
            isReadonly={isReadonly}
            isCircle={props.isCircle}
            isOnV4={props.isOnV4}
            isNew={isNew}
            onChange={props.onChange}
            parentCircleName={props.parentCircleName}
            policiesToMoveDown={props.policiesToMoveDown}
            rolesToMoveDown={props.rolesToMoveDown}
            policiesToMoveUp={props.policiesToMoveUp}
            rolesToMoveUp={props.rolesToMoveUp}
          />
        </Fragment>
      )}
    </div>
  )
}

Editor.defaultProps = {
  circle: null,
  destroyCircle: false,
  isProtected: false,
  isReadonly: false,
}

export default (createFragmentContainer(Editor, {
  proposal: graphql`
    fragment Editor_proposal on Proposal {
      circle {
        databaseId
        
        ...Accountabilities_circle
      }
    }
  `,
  organization: graphql`
    fragment Editor_organization on Organization {
      databaseId
      domainsLabel: termFor(name: "domains")
      purposeLabel: termFor(name: "purpose")
      showAccountabilities: featureEnabled(name: "accountabilities")
      showDomains: featureEnabled(name: "domains")
      showPurpose: featureEnabled(name: "purpose")

      roleTemplates(first: 100) {
        edges {
          node {
            id
          }
        }
      }

      ...RoleTemplateSelector_organization
      ...Accountabilities_organization
      ...WYSIWYGTextareaWithFeatureFlag_organization
    }
  `,
}): RelayFragmentContainer<typeof(Editor)>)
